import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import type { BaseButtonStyle } from "components/designV2/buttons/baseButton";
import { Checkbox } from "components/designV2/checkbox/checkbox";
import { InputField } from "components/designV2/inputfield/inputfield";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { ColorV2 } from "config/colors";
import { createCompanyUser } from "services/firebase/firebase.service";
import firebase from "services/firebase/firebase.config";
import i18n from "services/translation/i18n";
import { useAuthStore } from "stores/authStore/authStore";
import { validateEmail } from "web-apps/company/utils/utils";
import {
  ActionsRow,
  FieldsWrapper,
  StyledForm,
  StyledTitle,
  TermsLink,
  TermsSection,
  TermsText,
  TermsTextWrapper,
} from "./signUp.styled";

const SIGN_UP_BUTTON_STYLES: Partial<BaseButtonStyle> = {
  backgroundColor: ColorV2.PositiveGreen,
  hoverBackgroundColor: ColorV2.SimflexityGreen,
  activeBackgroundColor: ColorV2.SimflexityGreen,
  disabledBackgroundColor: ColorV2.PositiveGreenLight,
  disabledTextColor: ColorV2.White,
  disabledBorderColor: "transparent",
};

const openExternalLink = (url: string): void => {
  window.open(url, "_blank", "noopener,noreferrer");
};

export const SignUp: React.FC = () => {
  const { t } = useTranslation();
  const requiredLabel = t("System.Required").toLowerCase();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [signUpEmailError, setSignUpEmailError] = useState("");
  const [signUpPasswordError, setSignUpPasswordError] = useState("");
  const [showEmailError, setShowEmailError] = useState(false);
  const [showPasswordError, setShowPasswordError] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const history = useHistory();
  const [, , authDispatch] = useAuthStore();
  const trimmedEmail = email.trim();

  const emailError = validateEmail(email)
    ? signUpEmailError
    : t("RegisterCompany.InvalidEmail");
  const passwordError =
    password.length >= 6
      ? signUpPasswordError
      : t("RegisterCompany.PasswordMustBe6Characters");
  const lang = i18n.language;
  const normalizedLang = lang.toLowerCase();
  const isSwedish =
    normalizedLang === "sv" ||
    normalizedLang === "se" ||
    normalizedLang.startsWith("sv-") ||
    normalizedLang.startsWith("se-");

  const validate = () => {
    return Boolean(!emailError && !passwordError && isChecked);
  };

  const handleSignUp = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();

    if (isSubmitting) {
      return;
    }

    if (validate()) {
      setIsSubmitting(true);
      setIsPasswordVisible(false);

      try {
        const res = await createCompanyUser(trimmedEmail, password);

        if (res.user) {
          authDispatch({
            type: "ACCOUNT_SIGN_IN",
            payload: {
              firestoreUser: res.user,
            },
          });
        }

        history.push("/company/registration");
      } catch (error) {
        const firebaseError = error as firebase.auth.Error;

        switch (firebaseError.code) {
          case "auth/invalid-email":
            setSignUpEmailError(t("RegisterCompany.InvalidEmail"));
            setShowEmailError(true);
            break;
          case "auth/email-already-in-use":
            setSignUpEmailError(t("RegisterCompany.EmailInUse"));
            setShowEmailError(true);
            break;
          case "auth/weak-password":
            setSignUpPasswordError(t("RegisterCompany.WeakPassword"));
            setShowPasswordError(true);
            break;
          default:
            setSignUpPasswordError(t("RegisterCompany.SomethingWentWrong"));
            setShowPasswordError(true);
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <StyledForm onSubmit={handleSignUp}>
      <StyledTitle>{t("RegisterCompany.SignupTitle")}</StyledTitle>
      <FieldsWrapper>
        <InputField
          label={`${t("General.Email")} (${requiredLabel})`}
          type="email"
          value={email}
          placeholder={t("General.Email")}
          fullWidth
          hasError={showEmailError && Boolean(emailError)}
          errorText={emailError}
          onChange={(value) => {
            setSignUpEmailError("");
            setEmail(value);
          }}
          onBlur={() => setShowEmailError(true)}
          onClear={() => setEmail("")}
          disabled={isSubmitting}
        />
        <InputField
          label={`${t("General.Password")} (${requiredLabel})`}
          type={isPasswordVisible ? "text" : "password"}
          value={password}
          placeholder={t("General.Password")}
          fullWidth
          autoComplete="new-password"
          spellCheck={false}
          iconName={isPasswordVisible ? "v2/not-visible" : "v2/eye"}
          iconPosition="right"
          iconAriaLabel={t(
            isPasswordVisible ? "General.HidePassword" : "General.ShowPassword",
          )}
          onIconClick={() => setIsPasswordVisible((visible) => !visible)}
          hasError={showPasswordError && Boolean(passwordError)}
          errorText={passwordError}
          onChange={(value) => {
            setSignUpPasswordError("");
            setPassword(value);
          }}
          onBlur={() => setShowPasswordError(true)}
          onClear={() => setPassword("")}
          disabled={isSubmitting}
        />
      </FieldsWrapper>
      <TermsSection>
        <Checkbox
          checked={isChecked ? "checked" : "unchecked"}
          onChange={setIsChecked}
          disabled={isSubmitting}
        />
        <TermsTextWrapper>
          <TermsText>
            {t("RegisterCompany.CreatingAccountAgreeTermsOfService")}{" "}
            <TermsLink
              type="button"
              onClick={() => {
                openExternalLink(
                  isSwedish
                    ? "https://simflexity.com/sv/allmanna-villkor"
                    : "https://simflexity.com/en/terms",
                );
              }}
            >
              {t("RegisterCompany.TermsOfService")}
            </TermsLink>
            {t("RegisterCompany.TermsSeparator")}{" "}
            <TermsLink
              type="button"
              onClick={() => {
                openExternalLink(
                  isSwedish
                    ? "https://simflexity.com/sv/integritetspolicy"
                    : "https://simflexity.com/en/privacy",
                );
              }}
            >
              {t("RegisterCompany.PrivacyPolicy")}
            </TermsLink>
            {t("RegisterCompany.FinalTermsSeparator")}{" "}
            <TermsLink
              type="button"
              onClick={() => {
                openExternalLink(
                  isSwedish
                    ? "https://www.simflexity.com/sv/personuppgiftsbitradesavtal"
                    : "https://www.simflexity.com/en/data-processing-agreement",
                );
              }}
            >
              {t("RegisterCompany.DataProcessingAgreement")}
            </TermsLink>
          </TermsText>
        </TermsTextWrapper>
      </TermsSection>
      <ActionsRow>
        <SecondaryButton
          type="button"
          label={t("General.Cancel")}
          disabled={isSubmitting}
          onClick={() => history.push("./login")}
        />
        <PrimaryButton
          type="submit"
          label={t("RegisterCompany.SignUp")}
          disabled={!validate() || isSubmitting}
          isLoading={isSubmitting}
          styleOverrides={SIGN_UP_BUTTON_STYLES}
        />
      </ActionsRow>
    </StyledForm>
  );
};
